import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { TDispatch } from '../../../types/Thunk';
import {
  removeProfilePhoto,
  uploadProfilePhoto,
} from '../../../actions/AuthActions';
import AlertBlock from '../../AlertBlock';
import { useAuthenticatedImage } from '../../../hooks/useAuthenticatedImage';

const TARGET_SIZE = 200;
const JPEG_QUALITY = 0.9;

const cropToJpeg = (imageSrc: string, area: Area): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('decode_failed'));
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = TARGET_SIZE;
      canvas.height = TARGET_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas_failed'));
        return;
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        img,
        area.x, area.y, area.width, area.height,
        0, 0, TARGET_SIZE, TARGET_SIZE
      );
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = imageSrc;
  });

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

interface IProfilePhotoUploadProps {
  photoUrl: string | null | undefined;
  editable: boolean;
}

const ProfilePhotoUpload: React.FC<IProfilePhotoUploadProps> = ({
  photoUrl,
  editable,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<TDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photoSrc = useAuthenticatedImage(photoUrl);

  const [cropSource, setCropSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropSource(dataUrl);
    } catch {
      setError(t('profile.photo.upload.failed'));
    }
  };

  const handleCropCancel = () => {
    setCropSource(null);
  };

  const handleCropConfirm = async () => {
    if (!cropSource || !croppedAreaPixels) return;

    setBusy(true);
    try {
      const dataUrl = await cropToJpeg(cropSource, croppedAreaPixels);
      await dispatch(uploadProfilePhoto(dataUrl));
      setCropSource(null);
    } catch {
      setError(t('profile.photo.upload.failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await dispatch(removeProfilePhoto());
    } catch {
      setError(t('profile.photo.remove.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Avatar src={photoSrc} sx={{ width: 80, height: 80 }} />
      {editable && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant='outlined'
            size='small'
            onClick={handlePickFile}
            disabled={busy}
          >
            {busy ? <CircularProgress size={16} /> : t('profile.photo.upload')}
          </Button>
          {photoUrl && (
            <Button
              variant='text'
              color='error'
              size='small'
              onClick={handleRemove}
              disabled={busy}
            >
              {t('profile.photo.remove')}
            </Button>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept='image/*'
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>
      )}
      {error && (
        <Box sx={{ flexBasis: '100%' }}>
          <AlertBlock type='error'>{error}</AlertBlock>
        </Box>
      )}
      <Dialog
        open={Boolean(cropSource)}
        onClose={busy ? undefined : handleCropCancel}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>{t('profile.photo.crop.title')}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 320,
              backgroundColor: '#000',
            }}
          >
            {cropSource && (
              <Cropper
                image={cropSource}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape='round'
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant='caption'>
              {t('profile.photo.crop.zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.01}
              onChange={(_, value) => setZoom(value as number)}
              disabled={busy}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropCancel} disabled={busy}>
            {t('profile.photo.crop.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleCropConfirm}
            disabled={busy || !croppedAreaPixels}
          >
            {busy ? (
              <CircularProgress size={16} />
            ) : (
              t('profile.photo.crop.confirm')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePhotoUpload;
